import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { AdminFormDialog } from '@/components/shared/AdminFormDialog';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import { composeRuleCopy } from '@shared/email/commEmailView';
import { CopyGuide, MessagePreview } from './commCopyHelp';
import { buildTemplatePreview } from './commPreview';
import {
    EXPERIENCE_OPTIONS,
    RULE_CONDITION_SIGNALS,
    WATER_OPTIONS,
} from './commCatalog';
import { DestinationField } from './DestinationField';
import { formatUnknownError } from './commUi';
import { predicateFromConditions, type ConditionRow } from './RuleEditorDialog';

type Audience = 'people' | 'criteria' | 'all';
type Channel = 'email' | 'push';

interface BlastPerson {
    user_id: string;
    display_name: string | null;
    email: string | null;
}

interface BlastForm {
    channel: Channel;
    subject: string;
    body: string;
    ctaLabel: string;
    clickUrl: string;
    audience: Audience;
    people: BlastPerson[];
    conditions: ConditionRow[];
}

const EMPTY: BlastForm = {
    channel: 'email',
    subject: '',
    body: 'Hola {nombre},\n\n',
    ctaLabel: 'Abrir AiPeces',
    clickUrl: '/get-app',
    audience: 'people',
    people: [],
    conditions: [],
};

const fieldClass = 'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm';

export function PunctualComposer(props: {
    onCancel: () => void;
    onSent: (result: { notice: string; blastId: string | null }) => void;
}) {
    const queryClient = useQueryClient();
    const [form, setForm] = useState<BlastForm>(EMPTY);
    const [userQuery, setUserQuery] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [confirmCount, setConfirmCount] = useState<number | null>(null);

    const userSearch = useQuery({
        queryKey: ['admin', 'comm-blast-users', userQuery],
        queryFn: async () => {
            const { data, error: rpcError } = await dbClient.rpc<BlastPerson[]>('admin_search_users', {
                p_search_query: userQuery.trim(),
                p_search_by_email: true,
                p_result_limit: 8,
            });
            if (rpcError) throw rpcError;
            return normalizeRpcResponse(data);
        },
        enabled: form.audience === 'people' && userQuery.trim().length >= 2,
    });

    const send = useMutation({
        mutationFn: async () => {
            const channel = form.channel;
            const body = composeRuleCopy({
                text: form.body,
                primaryLabel: channel === 'email' ? form.ctaLabel : '',
                secondaryLabel: '',
                secondaryUrl: '',
            });
            const res = await apiClient.post<{
                blast_id?: string;
                audience_count: number;
                sent: number;
                failed: number;
                remaining: number;
            }>('/ops/comm-blast', {
                action: 'send',
                channel,
                name: form.subject.trim(),
                subject: form.subject.trim(),
                body,
                click_url: form.clickUrl.trim(),
                audience: form.audience,
                people: form.people,
                predicate: predicateFromConditions(form.conditions),
                confirm_all: form.audience === 'all',
            });
            if (res.error) throw new Error(res.error.message);
            return res.data ?? { audience_count: 0, sent: 0, failed: 0, remaining: 0 };
        },
        onSuccess: (result) => {
            setConfirmCount(null);
            setError(null);
            const queued = Number(result.remaining);
            const sentNow = Number(result.sent);
            const failed = Number(result.failed);
            const parts = [`Han salido ${sentNow}.`];
            if (failed > 0) parts.push(`No salieron ${failed}.`);
            if (queued > 0) {
                parts.push(`Quedan ${queued} en cola. Salen solos en el siguiente cuarto de hora.`);
            }
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-blasts'] });
            props.onSent({ notice: parts.join(' '), blastId: result.blast_id ?? null });
        },
        onError: (err: unknown) => {
            setConfirmCount(null);
            setError(formatUnknownError(err, 'No se pudo enviar'));
        },
    });

    const askSend = async () => {
        setError(null);
        if (!form.subject.trim() || !form.body.trim()) {
            setError('Escribe el asunto y el texto.');
            return;
        }
        if (form.audience === 'people' && form.people.length === 0) {
            setError('Elige al menos una persona.');
            return;
        }
        if (form.audience === 'criteria' && form.conditions.length === 0) {
            setError('Añade al menos un criterio.');
            return;
        }
        const res = await apiClient.post<{ count: number }>('/ops/comm-blast', {
            action: 'preview',
            channel: form.channel,
            name: form.subject.trim(),
            subject: form.subject.trim(),
            body: form.body.trim(),
            click_url: form.clickUrl.trim(),
            audience: form.audience,
            people: form.people,
            predicate: predicateFromConditions(form.conditions),
        });
        if (res.error) {
            setError(res.error.message);
            return;
        }
        const count = Number(res.data?.count ?? 0);
        if (!count) {
            setError('Nadie puede recibir este envío con esa audiencia.');
            return;
        }
        setConfirmCount(count);
    };

    const preview = useMemo(() => {
        const body = composeRuleCopy({
            text: form.body,
            primaryLabel: form.channel === 'email' ? form.ctaLabel : '',
            secondaryLabel: '',
            secondaryUrl: '',
        });
        return buildTemplatePreview({
            key: 'puntual',
            channel: form.channel,
            locale: 'es',
            variant: 'A',
            subject: form.subject,
            preheader: '',
            body,
            clickUrl: form.clickUrl,
            templates: [],
            signature: 'Alberto M., de AiPeces',
        });
    }, [form.body, form.channel, form.clickUrl, form.ctaLabel, form.subject]);

    const dirty = JSON.stringify(form) !== JSON.stringify(EMPTY);

    return (
        <>
            <AdminFormDialog
                open
                title="Nueva campaña"
                description="Sale una vez, a la lista de ese momento. Quien cumpla los criterios más tarde no lo recibe."
                dirty={dirty}
                saving={send.isPending}
                saveLabel="Enviar"
                savingLabel="Enviando…"
                error={error}
                maxWidthClass="w-[min(96rem,96vw)] max-w-[min(96rem,96vw)]"
                contentClassName="flex h-[min(92vh,980px)] max-h-[92vh] flex-col overflow-hidden p-6"
                bodyClassName="min-h-0 flex-1 space-y-0 overflow-hidden"
                onClose={props.onCancel}
                onSave={() => void askSend()}
            >
            <div className="grid h-full min-h-0 gap-6 lg:grid-cols-2">
                <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
                    <div className="flex gap-2">
                        {(['email', 'push'] as const).map((channel) => (
                            <button
                                key={channel}
                                type="button"
                                className={`rounded-lg px-3 py-1.5 text-sm ${form.channel === channel ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                                onClick={() => setForm((prev) => ({ ...prev, channel }))}
                            >
                                {channel === 'email' ? 'Correo' : 'Aviso'}
                            </button>
                        ))}
                    </div>
                    <label className="block text-sm">
                        Asunto
                        <input
                            className={fieldClass}
                            value={form.subject}
                            onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                        />
                    </label>
                    <label className="block text-sm">
                        Texto
                        <textarea
                            className={`${fieldClass} min-h-28 font-mono`}
                            value={form.body}
                            onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
                        />
                    </label>
                    <CopyGuide channel={form.channel} />
                    <div className="grid gap-3 sm:grid-cols-2">
                        {form.channel === 'email' && (
                            <label className="block text-sm">
                                Botón
                                <input
                                    className={fieldClass}
                                    value={form.ctaLabel}
                                    onChange={(e) => setForm((prev) => ({ ...prev, ctaLabel: e.target.value }))}
                                />
                            </label>
                        )}
                        <DestinationField
                            label="Destino al pulsar"
                            value={form.clickUrl}
                            onChange={(clickUrl) => setForm((prev) => ({ ...prev, clickUrl }))}
                            inputClassName={fieldClass}
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {(
                            [
                                ['people', 'Personas'],
                                ['criteria', 'Criterios de ahora'],
                                ['all', 'Toda la audiencia'],
                            ] as const
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={`rounded-lg px-3 py-1.5 text-sm ${form.audience === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                                onClick={() => setForm((prev) => ({ ...prev, audience: value }))}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {form.audience === 'people' && (
                        <PeoplePicker
                            query={userQuery}
                            people={form.people}
                            results={userSearch.data ?? []}
                            onQuery={setUserQuery}
                            onAdd={(person) =>
                                setForm((prev) =>
                                    prev.people.some((item) => item.user_id === person.user_id)
                                        ? prev
                                        : { ...prev, people: [...prev.people, person] }
                                )
                            }
                            onRemove={(userId) =>
                                setForm((prev) => ({
                                    ...prev,
                                    people: prev.people.filter((item) => item.user_id !== userId),
                                }))
                            }
                        />
                    )}
                    {form.audience === 'criteria' && (
                        <CriteriaEditor
                            conditions={form.conditions}
                            onChange={(conditions) => setForm((prev) => ({ ...prev, conditions }))}
                        />
                    )}
                </div>
                <div className="min-h-0 overflow-y-auto">
                    <MessagePreview
                        channel={form.channel}
                        subject={preview.subject}
                        bodyText={preview.bodyText}
                        html={preview.html}
                        missing={preview.missing}
                        clickUrl={form.clickUrl}
                    />
                </div>
            </div>
            </AdminFormDialog>
            <AdminConfirmDialog
                open={confirmCount != null}
                title={form.audience === 'all' ? 'Enviar a toda la audiencia' : 'Enviar esta campaña'}
                description={
                    confirmCount == null
                        ? null
                        : `Va a salir a ${confirmCount} ${confirmCount === 1 ? 'persona' : 'personas'}. La lista se cierra ahora.`
                }
                confirmLabel="Enviar"
                variant={form.audience === 'all' ? 'danger' : 'default'}
                confirmDisabled={send.isPending}
                onCancel={() => setConfirmCount(null)}
                onConfirm={() => send.mutate()}
            />
        </>
    );
}

function PeoplePicker(props: {
    query: string;
    people: BlastPerson[];
    results: BlastPerson[];
    onQuery: (value: string) => void;
    onAdd: (person: BlastPerson) => void;
    onRemove: (userId: string) => void;
}) {
    return (
        <div className="space-y-2">
            <label className="block text-sm">
                Buscar cuenta
                <input
                    className={fieldClass}
                    value={props.query}
                    placeholder="Nombre o correo"
                    onChange={(e) => props.onQuery(e.target.value)}
                />
            </label>
            {props.results.length > 0 && (
                <ul className="overflow-hidden rounded-lg border border-border">
                    {props.results.map((person) => (
                        <li key={person.user_id}>
                            <button
                                type="button"
                                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                                onClick={() => {
                                    props.onAdd(person);
                                    props.onQuery('');
                                }}
                            >
                                <span>{person.display_name || 'Sin nombre'}</span>
                                <span className="text-muted-foreground">{person.email}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {props.people.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                    {props.people.map((person) => (
                        <li key={person.user_id}>
                            <button
                                type="button"
                                className="rounded-full bg-muted px-3 py-1 text-xs"
                                onClick={() => props.onRemove(person.user_id)}
                            >
                                {person.display_name || person.email} ×
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function CriteriaEditor(props: {
    conditions: ConditionRow[];
    onChange: (conditions: ConditionRow[]) => void;
}) {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Quienes ahora cumplan</p>
                <button
                    type="button"
                    className="text-sm text-primary"
                    onClick={() =>
                        props.onChange([...props.conditions, { signal: 'capture_count', op: 'lte', value: '0' }])
                    }
                >
                    Añadir criterio
                </button>
            </div>
            {props.conditions.map((row, index) => {
                const signal = RULE_CONDITION_SIGNALS.find((item) => item.key === row.signal);
                const kind = signal?.kind ?? 'num';
                const setRow = (patch: Partial<ConditionRow>) =>
                    props.onChange(props.conditions.map((item, i) => (i === index ? { ...item, ...patch } : item)));
                return (
                    <div key={index} className="grid grid-cols-[1fr_auto_auto_auto] gap-2">
                        <select
                            className={fieldClass}
                            value={row.signal}
                            onChange={(e) => {
                                const next = RULE_CONDITION_SIGNALS.find((item) => item.key === e.target.value);
                                const op = next?.kind === 'bool' ? 'is_true' : next?.kind === 'text' ? 'eq' : 'lte';
                                setRow({ signal: e.target.value, op, value: '' });
                            }}
                        >
                            {RULE_CONDITION_SIGNALS.map((item) => (
                                <option key={item.key} value={item.key}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                        <select
                            className={fieldClass}
                            value={row.op}
                            onChange={(e) => setRow({ op: e.target.value as ConditionRow['op'] })}
                        >
                            {kind === 'bool' && (
                                <>
                                    <option value="is_true">sí</option>
                                    <option value="is_false">no</option>
                                </>
                            )}
                            {kind === 'num' && (
                                <>
                                    <option value="lte">como máximo</option>
                                    <option value="gte">como mínimo</option>
                                </>
                            )}
                            {kind === 'text' && <option value="eq">es</option>}
                        </select>
                        {kind === 'num' && (
                            <input
                                className={fieldClass}
                                type="number"
                                value={row.value}
                                onChange={(e) => setRow({ value: e.target.value })}
                            />
                        )}
                        {kind === 'text' && (
                            <select
                                className={fieldClass}
                                value={row.value}
                                onChange={(e) => setRow({ value: e.target.value })}
                            >
                                <option value="">—</option>
                                {(row.signal === 'water_type' ? WATER_OPTIONS : EXPERIENCE_OPTIONS).map((opt) => (
                                    <option key={opt.key} value={opt.key}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        )}
                        {kind === 'bool' && <span />}
                        <button
                            type="button"
                            className="mt-1 text-sm text-muted-foreground"
                            onClick={() => props.onChange(props.conditions.filter((_, i) => i !== index))}
                        >
                            Quitar
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
